import { ComponentProps, ReactNode, useMemo } from "react";
import { Product } from "@/api/product-api";
import { cn } from "@/libs/utils";
import Image from "next/image";
import PriceDisplay, {
  DiscontinuedLabel,
  isDiscontinued,
} from "@/components/tactsuit/PriceDisplay";
import { useCurrency } from "@/hooks/useCurrency";
import Link from "next/link";
import { Button, buttonVariants } from "@/v2-components/ui/button";
import ChevronRightIcon from "@/v2-components/core/icon/chevron-right-icon";
import { useTrans } from "@/utils/useTrans";
import { ProductUtil } from "@/utils/ProductUtil";
import MFMHorizontalOutline from "@/v2-components/core/icon/mfm-horizontal-outline";
import { Badge } from "@/v2-components/ui/badge";
import { useProductPrice } from "@/hooks/useProductPrice";
import { useCartWithProducts } from "@/store/useCartWithProducts";
import { GePrice } from "@/v2-components/GePrice";
import NotifyOrAddButton from "@/components/tactsuit/NotifyOrAddButton";
import { type VariantProps } from "class-variance-authority";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import MFMHorizontalFill from "@/v2-components/core/icon/mfm-horizontal-fill";
import { useProductTags } from "@/hooks/useProductTags";
import ProductTagBadge from "@/components/tactsuit/components/ProductTagBadge";
import { useProducts } from "@/services/product/useProductService";
import { NotifyMe } from "@/v2-components/pages/tactsuit/NotifyMe";
import TruckIcon from "@/v2-components/core/icon/truck-icon";
import { getProductDetailHref } from "@/libs/product/detail-href";
// 시안이 지정한 원본 글리프(`icons/general/local_mall`). 헤더 장바구니
// (`ShoppingBagIcon` = 머티리얼 `shopping_bag`)와 **다른 글리프**라 재사용하지 않는다.
import LocalMallIcon from "@/v2-components/core/icon/LocalMallIcon";
import TimeUtil from "@/utils/TimeUtil";
import { useLocale } from "@/store/locale-store";
import { Spinner } from "@/v2-components/ui/spinner";
import { NotifyOrBuy } from "@/v2-components/tact-suit-card-notify-or-buy";
import {
  TactSuitCardProvider,
  useTactSuitCardContext as useCardContext,
} from "@/v2-components/tact-suit-card-context";

interface BaseProps {
  children?: React.ReactNode;
}

export { useTactSuitCardContext } from "@/v2-components/tact-suit-card-context";

function Root({
  product,
  sku,
  theme = "light",
  children,
}: {
  product: Product;
  sku?: string;
  theme?: "light" | "dark";
} & BaseProps) {
  return (
    <TactSuitCardProvider value={{ product, sku, theme }}>
      {children}
    </TactSuitCardProvider>
  );
}

function Thumbnail({
  className,
  src,
  alt = "",
  ...props
}: Omit<React.ComponentProps<typeof Image>, "src" | "alt"> & {
  src?: string;
  alt?: string;
}) {
  const { product } = useCardContext();

  return (
    <Image
      className={cn(className)}
      src={src || product.thumbnail}
      alt={alt || product.name}
      {...props}
    />
  );
}

function Cover({
  className,
  mobileSrc,
  desktopSrc,
}: {
  className?: string;
  mobileSrc?: string;
  desktopSrc?: string;
}) {
  const { product } = useCardContext();
  const mobile = useIsMobileView();

  const src = mobile
    ? mobileSrc || product.bannerImage.mobileSrc || product.thumbnail
    : desktopSrc || product.bannerImage.desktopSrc || product.thumbnail;

  return (
    <Image
      className={cn("object-cover", className)}
      src={src}
      alt={product.name}
      fill
    />
  );
}

function Name({ className, name }: { className?: string; name?: string }) {
  const { product, theme } = useCardContext();

  return (
    <h2
      className={cn(
        "text-2xl text-coolgray-90",
        theme === "dark" && "text-white",
        className,
      )}
    >
      {name ?? product.name}
    </h2>
  );
}

function Slogan({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) {
  const { theme } = useCardContext();
  return (
    <h2
      className={cn(
        "text-lg font-500 text-coolgray-90",
        theme === "dark" && "text-white",
        className,
      )}
    >
      {children}
    </h2>
  );
}

/**
 * 선택된 SKU 를 **소유한 제품**의 skuOption 을 찾는다.
 *
 * ⚠️ SKU 가 카드의 `product` 에 없을 수 있다. 글러브 사이즈 세트(`all-size`)를 고르면
 * SKU 가 `tactglove-dk3-bundle-<color>-all-size` 로 **별도 제품** 소속이 된다.
 * `product.skuOptions` 만 뒤지면 못 찾아서 배송예정일·재고가 **조용히 사라진다**
 * (DK3 세트 선택 시 실제 발생).
 *
 * 판정 기준은 카트가 쓰는 것과 같은 `ProductUtil.getProductBySku` 로 맞춘다
 * (`BuyTactsuitDetailPageTemplate.makeCartItem`) — 표시와 실제 담기는 물건이
 * 갈리지 않게 하려면 같은 함수를 써야 한다.
 */
function useSkuOption(withFallback = false) {
  const { product, sku } = useCardContext();
  const { data: products } = useProducts();

  return useMemo(() => {
    // 폴백(`findProductSku`)은 sku 가 없을 때만 계산한다 — 렌더 본문에서 미리
    // 구하면 sku 가 있는 호출처(구매 레일 전체)에서 매 렌더 버려지는 비용이 된다.
    const target =
      sku ??
      (withFallback
        ? ProductUtil.findProductSku({ product, candidateSkus: [] })
        : null);
    if (!target) return undefined;
    return (
      product.skuOptions.find((opt) => opt.sku === target) ??
      ProductUtil.getProductBySku(products ?? [], target)?.skuOptions.find(
        (opt) => opt.sku === target,
      ) ??
      undefined
    );
  }, [product, sku, withFallback, products]);
}

function EstShippingDate({
  className,
  children,
  icon = <TruckIcon className="size-6" />,
}: React.PropsWithChildren<{ className?: string; icon?: ReactNode }>) {
  const { product, sku } = useCardContext();
  const { languageCode, locale } = useLocale();
  const { t } = useTrans(["tactsuit"]);

  // sku가 있으면 해당 옵션의 shippingInfo, 없으면 첫 번째 skuOption의 shippingInfo 사용
  const resolved = useSkuOption();
  const skuOption = sku ? resolved : product.skuOptions[0];
  // 재고 0 SKU 의 표지(9999-12-31)는 날짜가 아니다 → 그리지 않는다(`ProductUtil.getSkuShippingDate`).
  const shippingDate = ProductUtil.getSkuShippingDate(skuOption);

  const estimatedShippingDate = shippingDate
    ? TimeUtil.toShipDateString(shippingDate, locale || languageCode)
    : null;

  if (!estimatedShippingDate) {
    return null;
  }

  if (children) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {icon}
        {children}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {icon}
      <h4 className="text-coolgray-90">
        {t("Est. Shipping date: {{date}}", {
          date: estimatedShippingDate,
          // 폴백도 en 번역값과 같게 둔다 — 키는 옛 문구 그대로(키 이름 고정 규칙).
          defaultValue: `Est. Ship Date: ${estimatedShippingDate}`,
          // 키의 ':'가 i18next namespace separator로 오해되는 것 방지
          nsSeparator: false,
        })}
      </h4>
    </div>
  );
}

function Stock({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) {
  const { t } = useTrans(["tactsuit"]);

  const skuOption = useSkuOption(true);
  const stock = skuOption?.stock ?? null;

  if (!stock) {
    return null;
  }

  if (children) {
    return <div className={cn(className)}>{children}</div>;
  }

  const LOW_STOCK_THRESHOLD = 5;
  if (stock >= LOW_STOCK_THRESHOLD) {
    return null;
  }

  const text = t("Only {{stock}} left in stock", {
    stock,
    defaultValue: `Only ${stock} left in stock`,
  });

  return <h4 className={cn("text-red-60", className)}>{text}</h4>;
}

function Description({
  className,
  isFull,
  description,
}: {
  className?: string;
  isFull?: boolean;
  description?: string;
}) {
  const { product, theme } = useCardContext();

  return (
    <h4
      className={cn(
        "text-base text-coolgray-90",
        theme === "dark" && "text-white",
        className,
      )}
    >
      {description ?? (isFull ? product.desc : product.shortDesc)}
    </h4>
  );
}

function SaleBadge({ percent }: { className?: string; percent?: number }) {
  const { t } = useTrans(["tactsuit"]);
  const { product } = useCardContext();
  const { getProductPrice } = useCartWithProducts();
  const price = getProductPrice(product);

  const { displayDicountPercent } = useProductPrice(price);

  const percentOff = t("{{percent}}% off", {
    ns: "gnb",
    percent: percent ?? displayDicountPercent,
    defaultValue: `${percent ?? displayDicountPercent}% off`,
  });

  if (!percent && !displayDicountPercent) {
    return null;
  }

  return (
    // ⚠️ `normal-case` — `badgeVariants` 베이스에 `capitalize` 가 있어 그냥 두면
    // **"12% Off"** 가 된다. 시안은 소문자("10% off")이고 `cart-sheet/product-card`
    // 는 #5216 에서 이미 맞췄다 — 같은 번역 키가 두 표기로 나오던 상태였다.
    //
    // 전역(`badgeVariants` 의 `capitalize` 제거)은 `ui/badge` 를 쓰는 33개 파일이
    // 전부 영향권이라 디자이너 확인이 필요하다. 그 결정과 **무관하게** 두 곳을
    // 먼저 소문자로 통일해 불일치만 없앤다. 되돌릴 땐 이 클래스와 `product-card`
    // 쪽 한 줄만 지우면 된다.
    <Badge variant={"sale"} size={"sm"} className="normal-case">
      {percentOff}
    </Badge>
  );
}

function Price({ className }: { className?: string }) {
  const { product } = useCardContext();
  const { getProductPrice } = useCartWithProducts();
  const price = getProductPrice(product);

  return (
    <PriceDisplay
      className={cn(className)}
      price={price}
      productSlug={product.slug}
    />
  );
}

function BuyNow({
  className,
  variant = "blue-outline",
  label = "Buy Now",
  onClick,
  children,
  onAdd,
}: React.PropsWithChildren<{
  label?: string;
  onClick?: () => void;
  className?: string;
  variant?: VariantProps<typeof buttonVariants>["variant"];
  onAdd?: () => void;
}>) {
  const { product } = useCardContext();
  const { t } = useTrans(["tactsuit"]);

  return (
    <NotifyOrAddButton
      trigger="product_card"
      label={t(label, { ns: "gnb" })}
      variant={variant}
      className={className}
      productSlug={product.slug}
      onClick={onClick}
      onAdd={onAdd}
    >
      {children}
    </NotifyOrAddButton>
  );
}

function NotifyOrAddToBag({
  className,
  label = "Add to Bag",
  children,
  addToCart,
  disabled = false,
  iconOnly = false,
}: React.PropsWithChildren<{
  label?: string;
  className?: string;
  addToCart?: (e: any) => void;
  disabled?: boolean;
  /**
   * 라벨 대신 **40px 원형 장바구니 아이콘**으로 그린다 (시안 29006:52760 / 29006:53753).
   *
   * 결제 버튼이 비회원/회원으로 갈리면 한 행에 버튼이 3개가 되어 라벨이 다 안 들어간다 —
   * 실측(390 뷰포트, 하단 바): `Guest Checkout`·`Account Checkout` 이 **각 72px** 로
   * 눌려 있었고 `Add to Bag` 만 199px 를 먹었다. 아이콘이 그 199px 를 40px 로 돌려준다.
   *
   * ⚠️ 판정은 `useIsSplitCheckout()` 한 곳에서만 — 호출처가 `useUser()` 로 다시 계산하면
   * 결제 버튼의 분기와 어긋나 아이콘 1개 + 결제 1개가 될 수 있다.
   * ⚠️ **품절이면 무시된다**(아래 분기 순서). 품절 문구(`Notify me`)는 아이콘으로 대체할 수
   * 없고, 그때는 결제 버튼도 없어서 폭을 아낄 이유가 없다.
   */
  iconOnly?: boolean;
}>) {
  const { t } = useTrans(["tactsuit"]);
  const { product, sku } = useCardContext();
  const { data: products } = useProducts();
  const isSoldOut = ProductUtil.isProductSoldOut(
    products?.find((p) => p.slug === product.slug),
  );

  if (isSoldOut) {
    return (
      <NotifyMe
        productSku={sku}
        className={cn("px-6 text-coolgray-90", className)}
        productSlug={product.slug}
      />
    );
  }

  // 라벨이 사라지므로 `aria-label` 로 이름을 남긴다 — 아이콘만 있는 버튼은 스크린리더에
  // 이름이 없으면 "버튼" 으로만 읽힌다.
  const bagLabel = t(label, { ns: "gnb" });

  if (iconOnly) {
    return (
      <Button
        onClick={(e) => addToCart?.(e)}
        size={"square-sm"}
        disabled={disabled}
        shape={"pill"}
        // 시안은 테두리 `Cool Gray/20` · 채움 없음 · 아이콘 `Cool Gray/90` 이다.
        //
        // hover 는 **게스트 결제 버튼과 같은 색**(`blue-outline` → `bg-blue-60` + 흰 내용)
        // 으로 맞춘다. 종전 `darkgray-outline` 은 hover 에서 `coolgray-90`(거의 검정)이 차서
        // 옆 버튼보다 훨씬 어두웠다. 테두리·아이콘 색은 시안 값으로 덮는다 —
        // `blue-outline` 의 기본 `text-blue-60`·`outline-blue-60` 를 쓰면 안 된다.
        //
        // ⚠️ 아이콘 색이 `currentColor` 여야 hover 에서 같이 흰색이 된다(그 컴포넌트 주석).
        variant={"blue-outline"}
        aria-label={bagLabel}
        title={bagLabel}
        className={cn(
          "shrink-0 px-0 text-coolgray-90 outline-coolgray-20",
          className,
        )}
      >
        <LocalMallIcon />
        {!!children && children}
      </Button>
    );
  }

  return (
    <>
      <Button
        onClick={(e) => addToCart?.(e)}
        size={"sm"}
        disabled={disabled}
        shape={"pill"}
        variant={"blue-outline"}
        className={cn("px-6", className)}
      >
        {bagLabel}
        {!!children && children}
      </Button>
    </>
  );
}

function CheckOut({
  className,
  label = "Checkout",
  children,
  checkout,
  disabled = false,
  isCheckingOut = false,
}: React.PropsWithChildren<{
  label?: string;
  className?: string;
  checkout?: (e: any) => void;
  disabled?: boolean;
  isCheckingOut?: boolean;
}>) {
  const { t } = useTrans(["tactsuit"]);
  const { sku } = useCardContext();

  return (
    <>
      <Button
        onClick={(e) => checkout?.(e)}
        size={"sm"}
        disabled={disabled || isCheckingOut}
        shape={"pill"}
        variant={!!sku ? "blue-fill" : "blue-outline"}
        className={cn("px-6", className)}
      >
        {t(label, { ns: "gnb" })}
        {!!children && children}
        {isCheckingOut && <Spinner className="absolute scale-50" />}
      </Button>
    </>
  );
}

function PriceDetail({
  className,
  salePriceClassName,
}: {
  className?: string;
  /** 판매가만 다른 색/굵기로 쓸 때(구매 레일은 파랑 24px). 미지정 시 기존 카드 스타일. */
  salePriceClassName?: string;
}) {
  const { product, theme } = useCardContext();
  const { getProductPrice } = useCartWithProducts();
  const price = getProductPrice(product);
  const { countryCode } = useLocale();

  const {
    displayOriginalPrice,
    displayDiscountAmount,
    displaySalePrice,
    displayDicountPercent,
  } = useProductPrice(price);

  if (isDiscontinued(product.slug)) {
    return <DiscontinuedLabel className={className} />;
  }

  if (!price) return null;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-center gap-2",
        className,
      )}
    >
      <SaleBadge percent={displayDicountPercent} />
      <p
        className={cn(
          "font-600 text-coolgray-90",
          theme === "dark" && "text-white",
          salePriceClassName,
        )}
      >
        <GePrice
          amount={displaySalePrice}
          currency={price.currency}
          countryCode={countryCode}
        />
      </p>
      {!!displayDiscountAmount && (
        <p className={cn("text-coolgray-50 line-through")}>
          <GePrice
            amount={displayOriginalPrice}
            currency={price.currency}
            countryCode={countryCode}
          />
        </p>
      )}
    </div>
  );
}

function DiscountAmount({ className }: { className?: string }) {
  const { product } = useCardContext();
  const { getProductPrice } = useCartWithProducts();
  const { countryCode } = useLocale();
  const price = getProductPrice(product);

  const { displayOriginalPrice, displayDiscountAmount } =
    useProductPrice(price);

  if (isDiscontinued(product.slug)) return null;

  if (!price || !displayDiscountAmount) return null;

  return (
    <p className={cn("text-coolgray-50 line-through", className)}>
      <GePrice
        amount={displayOriginalPrice}
        currency={price.currency}
        countryCode={countryCode}
      />
    </p>
  );
}

function SalePrice({ className }: { className?: string }) {
  const { product, theme } = useCardContext();
  const { getProductPrice } = useCartWithProducts();
  const { countryCode } = useLocale();
  const price = getProductPrice(product);

  const { displaySalePrice } = useProductPrice(price);

  if (isDiscontinued(product.slug)) {
    return <DiscontinuedLabel className={className} />;
  }

  if (!price) return null;

  return (
    <p
      className={cn(
        "font-600 text-coolgray-90",
        theme === "dark" && "text-white",
        className,
      )}
    >
      <GePrice
        amount={displaySalePrice}
        currency={price.currency}
        countryCode={countryCode}
      />
    </p>
  );
}

function LearnMore({
  className,
  variant = "blue-text",
  icon = <ChevronRightIcon className="size-4.5" />,
  label = "Learn More",
}: {
  icon?: ReactNode;
  className?: string;
  variant?: VariantProps<typeof buttonVariants>["variant"];
  /**
   * 버튼 문구. **동작은 그대로 PDP 이동**이고 표기만 바꾼다 (#5381).
   *
   * ⚠️ **기본값은 `Learn More` 로 남긴다.** 이 컴포넌트는 6곳에서 쓰이는데 이번 요청은
   * `/tactsuit/` 한 페이지다(기획 확인 2026-08-26). 기본값을 바꾸면 홈 추천 결과 · 제품 비교 ·
   * PDP Tech Specs · `/shop/buy-tactsuit` 목록까지 같이 바뀐다.
   *
   * ⚠️ **namespace 는 `gnb` 로 하드코딩한다** — 형제 `BuyNow`·`NotifyOrAddToBag` 도 같다.
   * `useTrans` 배열의 해석 순서에 기대지 않는 것이 목적이고, 그건 하드코딩으로 달성된다.
   * (`labelNs` 프롭을 뒀다가 걷었다 — 넘기는 호출처가 0곳이고 형제 패턴과 어긋났다, #5391 리뷰.)
   *
   * ⚠️ 순서에 기대면 안 되는 이유: **`Learn More` 는 `gnb` 와 `tactsuit` 에 값이 다르다**
   * (jp 는 문구 자체, de·es·fr 은 첫 글자 대소문자). 4개 로케일 표기가 조용히 달라진다.
   */
  label?: string;
}) {
  const { product } = useCardContext();
  const { t } = useTrans(["tactsuit", "gnb"]);

  return (
    <Link
      // ⚠️ `/tactsuit/{slug}` 를 직접 조립하지 않는다. 그 라우트는 **전부 통합 PDP 로 가는
      // 307 리다이렉트**다(#5068 로 소개 페이지 제거 — 기기 7종 실측). 직접 쓰면 클릭마다
      // 왕복이 한 번 더 들고, 리다이렉트를 걷어내는 순간 전부 404 가 된다.
      // 목적지는 `detail-href` 단일 출처를 지난다(#5276).
      href={getProductDetailHref(product.slug)}
      className={cn(
        buttonVariants({
          variant: variant,
          size: "sm",
        }),
        className,
      )}
    >
      <span>{t(label, { ns: "gnb" })}</span>
      {!!icon && icon}
    </Link>
  );
}

const MetaBadge = ({
  className,
  filled,
  children,
}: React.PropsWithChildren<{
  className?: string;
  filled?: boolean;
}>) => {
  const { product } = useCardContext();

  if (!ProductUtil.isMadeForMeta(product.slug)) {
    return null;
  }

  if (children) {
    return children;
  }

  return filled ? (
    <MFMHorizontalFill className={cn(className)} />
  ) : (
    <MFMHorizontalOutline className={cn(className)} />
  );
};

const Tags = ({
  className,
  size,
}: {
  className?: string;
  size?: ComponentProps<typeof Badge>["size"];
}) => {
  const { product } = useCardContext();
  const { getProductTagsBySlug } = useProductTags();
  const tags = getProductTagsBySlug(product.slug) ?? [];

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {tags.map((tag) => (
        <ProductTagBadge size={size} key={tag} tag={tag} />
      ))}
    </div>
  );
};

const TactSuitCard = Object.assign(Root, {
  Thumbnail,
  Cover,
  Price,
  Slogan,
  LearnMore,
  MetaBadge,
  EstShippingDate,
  PriceDetail,
  Name,
  Tags,
  Description,
  BuyNow,
  NotifyOrBuy,
  SaleBadge,
  SalePrice,
  Stock,
  DiscountAmount,
  NotifyOrAddToBag,
  CheckOut,
});

export default TactSuitCard;
